import React, { useState } from "react";
import { useProfileData } from "../../../apis/profileApis";
import {
  useMerchantAllowedShippingChannels,
  useConnectMerchant3PLCredentialsMutation,
} from "../../../apis/integrationApis";
import toast from "react-hot-toast";
import {
  IconCheck,
  IconSettingsCode,
  IconX,
  IconPencil,
  IconFlask,
  IconRocket,
  IconChevronDown,
} from "@tabler/icons-react";

type CredentialField = {
  key: string;
  label: string;
  placeholder?: string;
  required: boolean;
  type?: "text" | "password" | "select";
  options?: { label: string; value: string }[];
};

const partnerFields: Record<string, CredentialField[]> = {
  Aramex: [
    { key: "AccountNumber", label: "Account Number", required: true, placeholder: "Enter Account Number" },
    { key: "AccountPin", label: "Account Pin", required: true, placeholder: "Enter Account Pin" },
    { key: "UserName", label: "Username", required: true, placeholder: "Enter Username" },
    { key: "Password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "AccountEntity", label: "Account Entity", required: true, placeholder: "Enter Account Entity" },
    { key: "AccountCountryCode", label: "Account Country Code", required: true, placeholder: "Enter Account Country Code" },
    {
      key: "PaymentType",
      label: "Payment Type",
      required: true,
      type: "select",
      placeholder: "Select Payment Type",
      options: [
        { label: "P (Prepaid)", value: "P" },
        { label: "C (Credit)", value: "C" },
        { label: "3 (Third Party)", value: "3" },
      ],
    },
    {
      key: "ProductGroup",
      label: "Product Group",
      required: true,
      type: "select",
      placeholder: "Select Product Group",
      options: [
        { label: "DOM (Domestic)", value: "DOM" },
        { label: "EXP (Express / International)", value: "EXP" },
      ],
    },
    {
      key: "ProductType",
      label: "Product Type",
      required: true,
      type: "select",
      placeholder: "Select Product Type",
      options: [
        { label: "CDS (Courier Domestic Service)", value: "CDS" },
        { label: "PDX (Priority Parcel Express)", value: "PDX" },
        { label: "PPX (Prepaid Parcel Express)", value: "PPX" },
        { label: "ONP (Overnight Parcel)", value: "ONP" },
        { label: "EPX (Economy Parcel Express)", value: "EPX" },
        { label: "DLV (Delivery)", value: "DLV" },
      ],
    },
  ],
  SMSA: [
    { key: "username", label: "Username", required: true, placeholder: "Enter SMSA username" },
    { key: "password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
  ],
  Naqel: [
    { key: "userName", label: "Username", required: true, placeholder: "Enter Naqel username" },
    { key: "password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "customerCode", label: "Customer Code", required: true, placeholder: "e.g. NL123456" },
    { key: "branchCode", label: "Branch Code", required: true, placeholder: "e.g. NL567899" },
  ],
  "Naqel Express": [
    { key: "userName", label: "Username", required: true, placeholder: "Enter Naqel username" },
    { key: "password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "customerCode", label: "Customer Code", required: true, placeholder: "e.g. NL122212" },
    { key: "branchCode", label: "Branch Code", required: true, placeholder: "e.g. NL312542" },
  ],
  "J&T Express": [
    { key: "customerCode", label: "Customer Code", required: true, placeholder: "Enter customer code" },
    { key: "apiAccount", label: "API Account", required: true, placeholder: "Enter API account" },
    { key: "privateKey", label: "Private Key", required: true, type: "password", placeholder: "Leave blank to keep existing" },
  ],
  Zid: [
    { key: "clientId", label: "Client ID", required: true, placeholder: "e.g. 1234" },
    { key: "clientSecret", label: "Client Secret", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "appId", label: "App ID", required: true, placeholder: "e.g. 1234" },
  ],
  Shopify: [
    { key: "shopDomain", label: "Shop Domain", required: true, placeholder: "e.g. myshop.myshopify.com" },
    { key: "appId", label: "API Key", required: true, placeholder: "e.g. s3gh5hbhv6***********" },
    { key: "secretKey", label: "API Secret Key", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "accessToken", label: "Admin Access Token", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "apiVersion", label: "API Version", required: true, placeholder: "e.g. 2024-07" },
  ],
  Shoapify: [
    { key: "shopDomain", label: "Shop Domain", required: true, placeholder: "e.g. myshop.myshopify.com" },
    { key: "appId", label: "API Key", required: true, placeholder: "e.g. s3gh5hbhv6***********" },
    { key: "secretKey", label: "API Secret Key", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "accessToken", label: "Admin Access Token", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "apiVersion", label: "API Version", required: true, placeholder: "e.g. 2024-07" },
  ],
};

const ShippingIntegrations: React.FC = () => {
  const { data: profile } = useProfileData();
  const clientId = profile?.id || profile?.clientId || "";

  const { data: allowedChannels, isLoading, refetch } = useMerchantAllowedShippingChannels(clientId);
  const connectMutation = useConnectMerchant3PLCredentialsMutation();

  const [selectedChannel, setSelectedChannel] = useState<any | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [environment, setEnvironment] = useState<"test" | "prod">("prod");

  const handleOpenConnect = (channel: any) => {
    setSelectedChannel(channel);
    setEnvironment(channel.environment || "prod");
    const companyName = channel.companyName || channel.title;
    const fields = partnerFields[companyName] || channel.credentialsFields || channel.credentialKeys || [];
    
    const existingCreds = channel.credentials || {};
    const initialData: Record<string, string> = {};

    fields.forEach((f: any) => {
      const key = typeof f === "string" ? f : f.key;
      initialData[key] = existingCreds[key] || "";
    });

    setFormData(initialData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedChannel) return;

    const connectionId = selectedChannel.connectionId || selectedChannel.id;

    connectMutation.mutate(
      {
        connectionId,
        credentials: {
          ...formData,
          clientId,
          channelId: selectedChannel.id,
        },
        environment,
      },
      {
        onSuccess: () => {
          toast.success(`${selectedChannel.companyName || selectedChannel.title} credentials connected successfully!`);
          setSelectedChannel(null);
          refetch();
        },
        onError: (err: any) => {
          toast.error(err?.message || "Failed to save credentials");
        },
      }
    );
  };

  const channelsList = Array.isArray(allowedChannels) ? allowedChannels : [];

  const getFieldsForSelectedChannel = (): CredentialField[] => {
    if (!selectedChannel) return [];
    const companyName = selectedChannel.companyName || selectedChannel.title;
    if (partnerFields[companyName]) {
      return partnerFields[companyName];
    }
    if (Array.isArray(selectedChannel.credentialsFields)) {
      return selectedChannel.credentialsFields.map((f: any) => ({
        key: f.key || f.name,
        label: f.label || f.name || f.key,
        required: Boolean(f.required),
        type: f.type || "text",
        placeholder: f.placeholder || "",
      }));
    }
    if (Array.isArray(selectedChannel.credentialKeys)) {
      return selectedChannel.credentialKeys.map((k: any) => ({
        key: typeof k === "string" ? k : k.key,
        label: typeof k === "string" ? k : k.label || k.key,
        required: true,
        type: "text",
        placeholder: `Enter ${typeof k === "string" ? k : k.label || k.key}`,
      }));
    }
    return [
      { key: "accountNumber", label: "Account Number / Customer Code", required: true, placeholder: "Enter Account Number" },
      { key: "apiKey", label: "API Key / User Key", required: true, placeholder: "Enter API Key" },
      { key: "apiSecret", label: "API Secret / Password", required: false, type: "password", placeholder: "Enter API Secret" },
    ];
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-6 shadow-sm">
      <div className="border-b border-gray-100 pb-4 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-800">3PL Shipping Integrations</h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure your shipping partner account credentials assigned by Admin.
          </p>
        </div>
        <div className="text-xs font-bold px-3 py-1.5 rounded-full bg-yellow-100 text-yellow-800">
          Allowed Channels: {channelsList.length}
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse"></div>
          ))}
        </div>
      ) : channelsList.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <p className="text-gray-700 font-semibold text-base">No Shipping Channels Assigned</p>
          <p className="text-gray-400 text-sm mt-1">
            Admin has not granted access to any 3PL shipping channels for your account yet.
          </p>
        </div>
      ) : (
        /* Channel Cards Grid matching system layout */
        <div className="grid 2xl:grid-cols-8 lg:grid-cols-6 md:grid-cols-4 grid-cols-2 gap-6 w-full pt-2">
          {channelsList.map((channel: any) => {
            const companyName = channel.companyName || channel.title;
            const isConnected = channel.status === "Connected" || channel.isConnected;

            return (
              <div
                key={channel.id}
                onClick={() => handleOpenConnect(channel)}
                className="flex flex-col items-center text-center cursor-pointer group"
              >
                {/* Logo Box matching system w-20 h-20 border-2 */}
                <div
                  className={`w-20 h-20 rounded-xl flex items-center justify-center border-2 transition-all p-2 bg-white relative ${
                    isConnected
                      ? "border-green-500 shadow-xs ring-2 ring-green-100"
                      : "border-slate-200 hover:border-black hover:bg-slate-50"
                  }`}
                >
                  {channel.companyLogo ? (
                    <img
                      src={channel.companyLogo}
                      alt={companyName}
                      className="w-full h-full object-contain p-1"
                    />
                  ) : (
                    <span className="font-extrabold text-slate-700 text-lg">
                      {(companyName || "3P")[0]}
                    </span>
                  )}

                  {isConnected && (
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center shadow-xs">
                      <IconCheck size={12} stroke={3} />
                    </div>
                  )}
                </div>

                {/* Company Name */}
                <span className="mt-2 font-bold text-xs text-slate-800 truncate max-w-full">
                  {companyName}
                </span>

                {/* Connect / Edit Button */}
                <span
                  className={`mt-1 text-[10px] font-bold px-2.5 py-0.5 rounded-full transition-all flex items-center gap-1 ${
                    isConnected
                      ? "bg-green-100 text-green-700 border border-green-200"
                      : "bg-[#131327] text-white hover:bg-gray-800"
                  }`}
                >
                  {isConnected ? (
                    <>
                      <IconPencil size={10} /> Edit Credentials
                    </>
                  ) : (
                    "Connect"
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Integration Credentials Modal matching system fields */}
      {selectedChannel && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-5 animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto border border-gray-100">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <div className="flex items-center gap-3">
                {selectedChannel.companyLogo && (
                  <img
                    src={selectedChannel.companyLogo}
                    alt={selectedChannel.companyName || selectedChannel.title}
                    className="w-8 h-8 object-contain"
                  />
                )}
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    {selectedChannel.status === "Connected" || selectedChannel.isConnected ? "Edit" : "Connect"} {selectedChannel.companyName || selectedChannel.title}
                  </h3>
                  <p className="text-xs text-gray-400">
                    Enter your carrier API account credentials
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedChannel(null)}
                className="text-gray-400 hover:text-gray-600 font-bold p-1 rounded-lg hover:bg-gray-100"
              >
                <IconX size={20} />
              </button>
            </div>

            {/* Environment Toggle (Test vs Live Prod) */}
            <div className="bg-slate-50 p-3 rounded-2xl border border-slate-200/80 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {environment === "test" ? (
                  <IconFlask size={18} className="text-amber-500" />
                ) : (
                  <IconRocket size={18} className="text-emerald-500" />
                )}
                <div>
                  <p className="text-xs font-bold text-slate-800">
                    Environment: <span className={environment === "test" ? "text-amber-600" : "text-emerald-600"}>{environment === "test" ? "Sandbox / Test Mode" : "Production (Live)"}</span>
                  </p>
                  <p className="text-[10px] text-slate-400">Select API endpoint mode for carrier integration</p>
                </div>
              </div>

              <div className="flex bg-slate-200/80 p-0.5 rounded-xl text-[11px] font-bold">
                <button
                  type="button"
                  onClick={() => setEnvironment("test")}
                  className={`px-3 py-1 rounded-lg transition-all ${
                    environment === "test" ? "bg-amber-500 text-white shadow-2xs" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Test
                </button>
                <button
                  type="button"
                  onClick={() => setEnvironment("prod")}
                  className={`px-3 py-1 rounded-lg transition-all ${
                    environment === "prod" ? "bg-emerald-600 text-white shadow-2xs" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Live
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-center gap-2 border-b border-yellow-300/40 pb-2">
                <div className="p-1.5 rounded-lg bg-yellow-100 text-yellow-800">
                  <IconSettingsCode size={16} />
                </div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-700">
                  {selectedChannel.companyName || selectedChannel.title} Account Credentials
                </p>
              </div>

              <div className="space-y-3">
                {getFieldsForSelectedChannel().map((field) => (
                  <div key={field.key}>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {field.type === "select" ? (
                      <div className="relative">
                        <select
                          value={formData[field.key] || ""}
                          onChange={(e) =>
                            setFormData({ ...formData, [field.key]: e.target.value })
                          }
                          className="w-full border border-gray-300 rounded-xl px-3.5 py-2 text-xs font-medium text-gray-900 focus:ring-2 focus:ring-black focus:outline-none shadow-2xs appearance-none pr-9 bg-white cursor-pointer"
                        >
                          <option value="" disabled>
                            {field.placeholder || `Select ${field.label}`}
                          </option>
                          {field.options?.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <IconChevronDown
                          size={16}
                          className="absolute right-3.5 top-2.5 pointer-events-none text-slate-400"
                        />
                      </div>
                    ) : (
                      <input
                        type={field.type || "text"}
                        required={field.required && !formData[field.key] && selectedChannel.status !== "Connected"}
                        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                        value={formData[field.key] || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, [field.key]: e.target.value })
                        }
                        className="w-full border border-gray-300 rounded-xl px-3.5 py-2 text-xs font-medium text-gray-900 focus:ring-2 focus:ring-black focus:outline-none shadow-2xs"
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setSelectedChannel(null)}
                  className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={connectMutation.isPending}
                  className="px-6 py-2 text-xs font-bold text-[#131327] bg-[#FBD037] hover:bg-yellow-400 rounded-xl transition-all shadow-xs disabled:opacity-50 flex items-center gap-2 cursor-pointer"
                >
                  {connectMutation.isPending ? "Connecting..." : "Connect Credentials"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingIntegrations;
